import { useQuery, QueryResult, FetchResult, MaybeMasked } from '@apollo/client'
import {
  Country,
  GetEmployeesWithLatestAndDraftScoreGridQueryVariables,
  GetEmployeesWithLatestAndDraftScoreGridQuery,
  GetDraftOrLatestScoreGridByEmployeeQuery,
  GetDraftOrLatestScoreGridByEmployeeQueryVariables,
  DraftScoreGridInput,
  CreateOrUpdateDraftScoreGridMutation,
  CreateOrUpdateDraftScoreGridMutationVariables,
  ScoreGridExcelDataInput,
  ImportScoreGridsMutationVariables,
  ImportScoreGridsMutation,
  GetLatestScoreGridByEmployeeQuery,
  GetLatestScoreGridByEmployeeQueryVariables,
  FindCurrentBusinessUnitsByCompanyIdQuery,
  FindCurrentBusinessUnitsByCompanyIdQueryVariables,
} from '../../types'
import {
  EMPLOYEES_WITH_LATEST_AND_DRAFT_SCORE_GRID,
  DRAFT_OR_LATEST_SCORE_GRID_BY_EMPLOYEE,
  LATEST_SCORE_GRID_BY_EMPLOYEE,
  FIND_BUSINESS_UNITS_BY_COMPANY,
} from './Queries'
import { CREATE_OR_UPDATE_DRAFT_SCORE_GRID, IMPORT_EXCEL } from './Mutations'
import { client } from '../..'

export const useEmployeesWithLatestAndDraftScoreGrid = (
  companyId: string,
  country: Country,
): QueryResult<GetEmployeesWithLatestAndDraftScoreGridQuery, GetEmployeesWithLatestAndDraftScoreGridQueryVariables> => {
  return useQuery<GetEmployeesWithLatestAndDraftScoreGridQuery, GetEmployeesWithLatestAndDraftScoreGridQueryVariables>(
    EMPLOYEES_WITH_LATEST_AND_DRAFT_SCORE_GRID,
    {
      variables: { companyId, country },
      fetchPolicy: 'network-only',
    },
  )
}

export const useDraftOrLatestScoreGridByEmployee = (
  companyId: string,
  country: Country,
  employeeId: string,
): QueryResult<GetDraftOrLatestScoreGridByEmployeeQuery, GetDraftOrLatestScoreGridByEmployeeQueryVariables> => {
  return useQuery<GetDraftOrLatestScoreGridByEmployeeQuery, GetDraftOrLatestScoreGridByEmployeeQueryVariables>(
    DRAFT_OR_LATEST_SCORE_GRID_BY_EMPLOYEE,
    {
      variables: { companyId, country, employeeId },
      fetchPolicy: 'network-only',
    },
  )
}

export const useLatestScoreGridByEmployee = (
  companyId: string,
  country: Country,
  employeeId: string,
): QueryResult<GetLatestScoreGridByEmployeeQuery, GetLatestScoreGridByEmployeeQueryVariables> => {
  return useQuery<GetLatestScoreGridByEmployeeQuery, GetLatestScoreGridByEmployeeQueryVariables>(LATEST_SCORE_GRID_BY_EMPLOYEE, {
    variables: { companyId, country, employeeId },
    fetchPolicy: 'network-only',
  })
}

export const createOrUpdateDraftScoreGrid = async (
  draftScoreGridInput: DraftScoreGridInput,
  companyId: string,
  country: Country,
  employeeId: string,
): Promise<FetchResult<CreateOrUpdateDraftScoreGridMutation>> => {
  return client.mutate<CreateOrUpdateDraftScoreGridMutation, CreateOrUpdateDraftScoreGridMutationVariables>({
    mutation: CREATE_OR_UPDATE_DRAFT_SCORE_GRID,
    variables: {
      draftScoreGridInput,
      companyId,
      country,
      employeeId,
    },
    refetchQueries: [
      {
        query: EMPLOYEES_WITH_LATEST_AND_DRAFT_SCORE_GRID,
        variables: {
          companyId,
          country,
        },
      },
    ],
  })
}

export const importExcel = async (
  companyId: string,
  buIds: number[],
  country: Country,
  excelData: ScoreGridExcelDataInput[],
): Promise<FetchResult<MaybeMasked<ImportScoreGridsMutation>>> => {
  return client.mutate<ImportScoreGridsMutation, ImportScoreGridsMutationVariables>({
    mutation: IMPORT_EXCEL,
    variables: {
      companyId,
      buIds,
      country,
      excelData,
    },
    refetchQueries: [
      {
        query: EMPLOYEES_WITH_LATEST_AND_DRAFT_SCORE_GRID,
        variables: {
          companyId,
          country,
        },
      },
    ],
  })
}

export const useCurrentBusinessUnitsByCompany = (
  companyId: string,
  country: Country,
): QueryResult<FindCurrentBusinessUnitsByCompanyIdQuery, FindCurrentBusinessUnitsByCompanyIdQueryVariables> => {
  return useQuery<FindCurrentBusinessUnitsByCompanyIdQuery, FindCurrentBusinessUnitsByCompanyIdQueryVariables>(
    FIND_BUSINESS_UNITS_BY_COMPANY,
    {
      variables: { companyId, country },
      fetchPolicy: 'network-only',
    },
  )
}
